import { PayloadAction } from '@reduxjs/toolkit';

import { AppState, MultiTechAreaGroup } from 'editor/src/store/app/types';

import { isSameTechnology } from 'editor/src/component/EditorArea/EditorAreaControls/BottomControls/PageSelector/SpreadThumbnail/techInfo';

const setMultiTechAreaGroupsReducer = (
  state: AppState,
  {
    payload,
  }: PayloadAction<{
    areaGroups: MultiTechAreaGroup[];
    // GAPI-30848 (Bug B): caller may pre-compute the initial per-area tech
    // (e.g. from the loaded design's spread names) so the strip reflects the
    // customer's saved per-area picks on reopen. When omitted, fall back to
    // the legacy "first option wins" default.
    selectedTechByArea?: Record<string, string>;
  }>,
) => {
  state.multiTechAreaGroups = payload.areaGroups;
  if (payload.selectedTechByArea) {
    state.selectedTechByArea = selectableTechByArea(payload.areaGroups, payload.selectedTechByArea);
    return;
  }
  const next: Record<string, string> = {};
  payload.areaGroups.forEach((g) => {
    const selectable = g.options.find((option) => !option.disabled) ?? g.options[0];
    next[g.canonicalName] = selectable?.technology ?? '';
  });
  state.selectedTechByArea = next;
};

/**
 * Keep every area on a technology it can actually use. A selection goes bad in
 * two ways: the technology is still offered but became unavailable once another
 * area was designed, or the host dropped it from the area entirely. Both must be
 * repaired — this map is posted back to the host and decides what each variant
 * publishes, so a value that no option backs would ship as a print location the
 * product does not sell.
 *
 * Compared with `isSameTechnology`, not `===`: the host sends lowercase and the
 * editor's own derivation returns capitalised.
 */
function selectableTechByArea(
  areaGroups: MultiTechAreaGroup[],
  selected: Record<string, string>,
): Record<string, string> {
  const repaired = { ...selected };
  areaGroups.forEach((group) => {
    const current = group.options.find((option) => isSameTechnology(option.technology, repaired[group.canonicalName]));
    if (current && !current.disabled) {
      return;
    }
    const fallback = group.options.find((option) => !option.disabled);
    if (fallback) {
      repaired[group.canonicalName] = fallback.technology;
    }
  });
  return repaired;
}

export default setMultiTechAreaGroupsReducer;
